import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/themes/light-border.css';
import { LuChevronDown, LuChevronRight, LuArrowRight } from 'react-icons/lu';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/utilityApiConfig';
import { getSelectedUtility } from '../../utils/utilitySelection';
import { getUtilityWorkflowUiState, updateUtilityWorkflowUiState } from '../../utils/utilityWorkflowUiState';
import { useUtilityMdhdAnalysis } from '../../contexts/UtilityMdhdAnalysisContext';

const SCENARIO_INPUTS = [
  { label: 'Rate plan', path: '/utility/workflow/inputs/rate-plan' },
  { label: 'Managed charging', path: '/utility/workflow/inputs/managed-charging' },
  { label: 'Vehicle pricing', path: '/utility/workflow/inputs/vehicle-pricing' },
  { label: 'Policy', path: '/utility/workflow/inputs/policy' },
  { label: 'Charging infra', path: '/utility/workflow/inputs/charging-infra' },
  { label: 'Season/weather', path: '/utility/workflow/inputs/season-weather' },
  { label: 'Demographics', path: '/utility/workflow/inputs/demographics' },
  { label: 'Travel survey', path: '/utility/workflow/inputs/travel-survey' },
];

const SYSTEM_MODELS = [
  'EV Adoption Trend',
  'EV Travel Patterns',
  'EV Charging Behaviors',
];

/** Which Scenario Input labels primarily drive each System Model (for tooltip and hover highlight). */
const MODEL_INPUT_DEPENDENCIES = {
  'EV Adoption Trend': ['Demographics', 'Policy', 'Vehicle pricing'],
  'EV Travel Patterns': ['Travel survey', 'Demographics', 'Season/weather'],
  'EV Charging Behaviors': ['Rate plan', 'Managed charging', 'Charging infra', 'Season/weather'],
};

const WORKFLOW_STEPS = [
  { step: 1, title: 'Scenario Inputs', description: 'Configure assumptions and inputs for your scenario.' },
  { step: 2, title: 'System Models', description: 'Models use inputs to produce EV adoption, travel, and charging outputs.' },
  { step: 3, title: 'Load Forecasts', description: 'Get load forecasts and map to hexagon or feeder.' },
];

const VEHICLE_CATEGORIES = [
  { id: 'passenger-ev', label: 'Passenger EV' },
  { id: 'mdhd', label: 'MDHD Trucks' },
  { id: 'transit', label: 'Transit Bus' },
];

const MDHD_SCENARIO_INPUTS = [
  {
    label: 'Rate plan',
    path: '/utility/workflow/mdhd/inputs/rate-plan',
    description: 'Select/configure MDHD EV TOU rates for the logged-in utility.',
  },
  {
    label: 'Charging infrastructure',
    path: '/utility/workflow/mdhd/inputs/charging-infra',
    description: 'Configure charger assumptions; station overlays are in progress.',
    inDevelopment: true,
  },
  {
    label: 'Managed charging',
    path: '/utility/workflow/mdhd/inputs/managed-charging',
    description: 'Configure unmanaged/passive/active behavior; V2G controls are in progress.',
    inDevelopment: true,
  },
  {
    label: 'Vehicle configuration',
    path: '/utility/workflow/mdhd/inputs/vehicle-configuration',
    description: 'Select up to 5 MDHD vehicle models from the vehicle database.',
  },
  {
    label: 'Weather',
    path: '/utility/workflow/mdhd/inputs/season-weather',
    description: 'Review weather curves; utility-region upload workflow is in progress.',
  },
];

const MDHD_SYSTEM_MODELS = ['EV Adoption Trend', 'EV Charging Behavior'];

function MdhdVehicleSimulationPreview() {
  return (
    <div className="flex flex-col lg:flex-row items-stretch gap-0 mb-2">
      <div className="flex-1 min-w-0 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-blue-600 font-nunito">Step 1</div>
        <h3 className="text-lg font-semibold text-gray-800 mb-1 font-nunito">{WORKFLOW_STEPS[0].title}</h3>
        <p className="text-sm text-gray-500 mb-4 font-nunito">Configure MDHD-specific scenario assumptions.</p>
        <div className="space-y-3">
          {MDHD_SCENARIO_INPUTS.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className="block w-full px-4 py-3 rounded-xl border font-ui text-sm transition-colors bg-white border-gray-200 text-gray-800 hover:border-blue-400 hover:bg-blue-50/50"
            >
              <div className="flex items-center gap-2">
                <span className="font-medium">{item.label}</span>
                {item.inDevelopment && (
                  <span className="inline-flex items-center rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">
                    in development
                  </span>
                )}
              </div>
              {item.description && (
                <p className="mt-1 text-xs text-gray-500">{item.description}</p>
              )}
            </Link>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-center py-4 lg:py-0 lg:px-2 shrink-0" aria-hidden="true">
        <LuArrowRight className="w-8 h-8 text-blue-500 shrink-0" />
        <span className="sr-only">Data flow</span>
      </div>

      <div className="flex-1 min-w-0 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-blue-600 font-nunito">Step 2</div>
        <h3 className="text-lg font-semibold text-gray-800 mb-1 font-nunito">{WORKFLOW_STEPS[1].title}</h3>
        <p className="text-sm text-gray-500 mb-4 font-nunito">Run MDHD system model outputs.</p>
        <div className="space-y-3">
          {MDHD_SYSTEM_MODELS.map((name) => {
            const isAdoptionTrend = name === 'EV Adoption Trend';
            const isEvChargingBehavior = name === 'EV Charging Behavior';
            const content = (
              <span className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-800 font-medium font-nunito text-sm min-h-[48px] flex items-center hover:border-blue-300 hover:bg-blue-50/30 transition-colors">
                {name}
              </span>
            );
            return isAdoptionTrend ? (
              <Link
                key={name}
                to="/utility/workflow/system-models/mdhd/adoption-trend"
                className="block w-full cursor-pointer"
              >
                {content}
              </Link>
            ) : isEvChargingBehavior ? (
              <Link
                key={name}
                to="/utility/workflow/system-models/mdhd/ev-charging-behavior"
                className="block w-full cursor-pointer"
              >
                {content}
              </Link>
            ) : (
              <div key={name} className="block w-full cursor-default">
                {content}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function WorkflowMainContent({
  selectedVehicleCategories,
  onVehicleCategoryToggle,
  onLoadForecastsClick,
  loadForecastsChoice,
  feederMapFile,
  onStartAnalysisClick,
  analysisLoading,
  analysisError,
  mdhdUtilityOptions,
  selectedMdhdUtility,
  onSelectedMdhdUtilityChange,
}) {
  const [activeTab, setActiveTab] = useState(() => getUtilityWorkflowUiState().workflow?.activeTab || 'passenger-ev');
  const [hoveredModel, setHoveredModel] = useState(null);

  const isSelected = (id) => selectedVehicleCategories.includes(id);
  const highlightedInputLabels = hoveredModel ? (MODEL_INPUT_DEPENDENCIES[hoveredModel] || []) : [];

  useEffect(() => {
    updateUtilityWorkflowUiState('workflow', {
      activeTab,
      selectedVehicleCategories,
    });
  }, [activeTab, selectedVehicleCategories]);

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-800 mb-2 font-ui">Workflow</h1>
      <p className="text-gray-600 mb-2 font-ui">
        Scenario inputs drive system models; models produce load forecasts.
      </p>
      <p className="text-gray-600 mb-6 font-ui">
        Select vehicle categories to include in the analysis. Start analysis runs for all selected categories.
      </p>

      <div className="mb-8 rounded-xl border border-gray-200 bg-white p-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Utility (MDHD analysis)
        </label>
        <select
          value={selectedMdhdUtility}
          onChange={(e) => onSelectedMdhdUtilityChange(e.target.value)}
          className="w-full max-w-xl px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          {mdhdUtilityOptions.length === 0 && <option value="">No utilities available</option>}
          {mdhdUtilityOptions.map((utilityName) => (
            <option key={utilityName} value={utilityName}>
              {utilityName}
            </option>
          ))}
        </select>
        <p className="mt-2 text-xs text-gray-500">
          This utility selection drives the MDHD adoption trend and Start analysis run.
        </p>
      </div>

      <div className="mb-6 flex flex-wrap gap-3 border-b border-gray-200 pb-4">
        {VEHICLE_CATEGORIES.map((category) => (
          <button
            key={category.id}
            type="button"
            onClick={() => setActiveTab(category.id)}
            className={`inline-flex items-center gap-3 rounded-xl border px-4 py-2 text-sm font-medium transition-colors font-ui ${
              activeTab === category.id
                ? 'border-blue-300 bg-blue-50 text-blue-800'
                : 'border-gray-200 bg-white text-gray-700 hover:border-blue-200 hover:bg-blue-50/40'
            }`}
          >
            <span className="flex items-center" onClick={(e) => e.stopPropagation()}>
              <input
                type="checkbox"
                checked={isSelected(category.id)}
                onChange={() => onVehicleCategoryToggle(category.id)}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                aria-label={`Include ${category.label} in analysis`}
              />
            </span>
            <span>{category.label}</span>
            {isSelected(category.id) && (
              <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[11px] text-blue-700">
                selected
              </span>
            )}
          </button>
        ))}
      </div>

      {activeTab === 'passenger-ev' && (
        <div className="mb-6">
          <div className="mt-4 pl-4 border-l-2 border-gray-200">
            {/* Flow pipeline: Step 1 → Step 2 → Step 3 */}
            <div className="flex flex-col lg:flex-row items-stretch gap-0 mb-8">
              {/* Step 1: Scenario Inputs */}
              <div className="flex-1 min-w-0 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-blue-600 font-ui">
                  Step 1
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-1 font-ui">{WORKFLOW_STEPS[0].title}</h3>
                <p className="text-sm text-gray-500 mb-4 font-ui">{WORKFLOW_STEPS[0].description}</p>
                <div className="space-y-3">
                  {SCENARIO_INPUTS.map((item) => {
                    const isHighlighted = highlightedInputLabels.includes(item.label);
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`block w-full px-4 py-3 rounded-xl border font-ui text-sm font-medium transition-colors ${
                          isHighlighted
                            ? 'border-blue-400 bg-blue-50 text-blue-800 ring-1 ring-blue-200'
                            : 'bg-white border-gray-200 text-gray-800 hover:border-blue-400 hover:bg-blue-50/50'
                        }`}
                      >
                        {item.label}
                      </Link>
                    );
                  })}
                </div>
              </div>

              <div className="flex items-center justify-center py-4 lg:py-0 lg:px-2 shrink-0" aria-hidden="true">
                <LuArrowRight className="w-8 h-8 text-blue-500 shrink-0" />
                <span className="sr-only">Data flow</span>
              </div>

              {/* Step 2: System Models */}
              <div className="flex-1 min-w-0 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-blue-600 font-ui">
                  Step 2
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-1 font-ui">{WORKFLOW_STEPS[1].title}</h3>
                <p className="text-sm text-gray-500 mb-4 font-ui">{WORKFLOW_STEPS[1].description}</p>
                <div className="space-y-3">
                  {SYSTEM_MODELS.map((name) => {
                    const drivenBy = MODEL_INPUT_DEPENDENCIES[name] || [];
                    const tooltipContent = (
                      <div className="text-sm font-ui p-1">
                        <span className="font-semibold text-gray-800">Driven by:</span>
                        <ul className="mt-1 list-disc list-inside text-gray-700">
                          {drivenBy.map((label) => (
                            <li key={label}>{label}</li>
                          ))}
                        </ul>
                      </div>
                    );
                    const isAdoptionTrend = name === 'EV Adoption Trend';
                    const content = (
                      <span
                        onMouseEnter={() => setHoveredModel(name)}
                        onMouseLeave={() => setHoveredModel(null)}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-800 font-medium font-ui text-sm min-h-[48px] flex items-center hover:border-blue-300 hover:bg-blue-50/30 transition-colors"
                      >
                        {name}
                      </span>
                    );
                    return (
                      <Tippy key={name} content={tooltipContent} placement="top" theme="light-border">
                        {isAdoptionTrend ? (
                          <Link
                            to="/utility/workflow/system-models/passenger-ev/adoption-trend"
                            className="block w-full cursor-pointer"
                          >
                            {content}
                          </Link>
                        ) : (
                          <div className="block w-full cursor-default">{content}</div>
                        )}
                      </Tippy>
                    );
                  })}
                </div>
              </div>

              <div className="flex items-center justify-center py-4 lg:py-0 lg:px-2 shrink-0" aria-hidden="true">
                <LuArrowRight className="w-8 h-8 text-blue-500 shrink-0" />
                <span className="sr-only">Data flow</span>
              </div>

              {/* Step 3: Load Forecasts */}
              <div className="flex-1 min-w-0 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-blue-600 font-ui">
                  Step 3
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-1 font-ui">{WORKFLOW_STEPS[2].title}</h3>
                <p className="text-sm text-gray-500 mb-4 font-ui">{WORKFLOW_STEPS[2].description}</p>
                <button
                  type="button"
                  onClick={onLoadForecastsClick}
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl hover:border-blue-400 hover:bg-blue-50/50 transition-colors text-left font-ui text-sm font-medium text-gray-800"
                >
                  {loadForecastsChoice === 'feeder' && feederMapFile
                    ? `Map to feeder (${feederMapFile.name})`
                    : loadForecastsChoice === 'hexagon'
                    ? 'Map to standard hexagon'
                    : 'Select mapping...'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'mdhd' && (
        <div className="mb-6">
          <div className="mt-4 pl-4 border-l-2 border-gray-200">
            <MdhdVehicleSimulationPreview />
          </div>
        </div>
      )}

      {activeTab === 'transit' && (
        <div className="mb-6">
          <div className="mt-4 pl-4">
            <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
              <p className="text-gray-600 font-ui">Coming Soon</p>
            </div>
          </div>
        </div>
      )}

      <div className="mt-8 flex flex-wrap items-center gap-4">
        <button
          type="button"
          onClick={onStartAnalysisClick}
          disabled={analysisLoading}
          className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-colors font-ui"
        >
          {analysisLoading ? 'Starting analysis...' : 'Start analysis'}
        </button>
        <span className="text-sm text-gray-600 font-ui">
          Runs the MDHD simulation directly using the saved workflow inputs.
        </span>
        {analysisError && (
          <div className="basis-full rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {analysisError}
          </div>
        )}
      </div>
    </div>
  );
}

function UtilityWorkflow() {
  const navigate = useNavigate();
  const {
    analysisLoading,
    analysisError,
    startAnalysis,
  } = useUtilityMdhdAnalysis();
  const [loadForecastsChoice, setLoadForecastsChoice] = useState(null);
  const [feederMapFile, setFeederMapFile] = useState(null);
  const [showLoadForecastsModal, setShowLoadForecastsModal] = useState(false);
  const [showCaseNameModal, setShowCaseNameModal] = useState(false);
  const [caseName, setCaseName] = useState('');
  const [selectedVehicleCategories, setSelectedVehicleCategories] = useState(() => {
    const stored = getUtilityWorkflowUiState().workflow?.selectedVehicleCategories;
    if (Array.isArray(stored) && stored.length > 0) return stored;
    return VEHICLE_CATEGORIES.map((category) => category.id);
  });
  const [mdhdUtilityOptions, setMdhdUtilityOptions] = useState([]);
  const [selectedMdhdUtility, setSelectedMdhdUtility] = useState(() => {
    const workflowUiState = getUtilityWorkflowUiState();
    const stored = workflowUiState?.mdhdAdoptionTrend?.selectedUtility;
    const fallback = getSelectedUtility()?.utilityName || '';
    return stored || fallback;
  });

  useEffect(() => {
    updateUtilityWorkflowUiState('mdhdAdoptionTrend', {
      selectedUtility: selectedMdhdUtility,
    });
  }, [selectedMdhdUtility]);

  useEffect(() => {
    const run = async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) return;
        const res = await axios.get(API_ENDPOINTS.getMdhdForecastCurves, {
          headers: { Authorization: `Token ${token}` },
        });
        const utilities = Array.isArray(res.data?.utilities) ? res.data.utilities : [];
        setMdhdUtilityOptions(utilities);
        if (!utilities.length) return;
        if (!selectedMdhdUtility || !utilities.includes(selectedMdhdUtility)) {
          const preferred = getSelectedUtility()?.utilityName;
          const nextUtility = preferred && utilities.includes(preferred) ? preferred : utilities[0];
          setSelectedMdhdUtility(nextUtility);
        }
      } catch {
        setMdhdUtilityOptions([]);
      }
    };
    run();
  }, []);
  const handleVehicleCategoryToggle = (categoryId) => {
    setSelectedVehicleCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleLoadForecastsClick = () => {
    setShowLoadForecastsModal(true);
  };

  const handleStartAnalysisClick = () => {
    startAnalysis();
  };

  const handleCaseNameSubmit = (name) => {
    setShowCaseNameModal(false);
    setCaseName('');
    const finalName = (name && name.trim()) ? name.trim() : 'Untitled case';
    navigate('/utility/scenarios', {
      state: { caseName: finalName, selectedVehicleCategories },
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 font-ui">
      <WorkflowMainContent
        selectedVehicleCategories={selectedVehicleCategories}
        onVehicleCategoryToggle={handleVehicleCategoryToggle}
        onLoadForecastsClick={handleLoadForecastsClick}
        loadForecastsChoice={loadForecastsChoice}
        feederMapFile={feederMapFile}
        onStartAnalysisClick={handleStartAnalysisClick}
        analysisLoading={analysisLoading}
        analysisError={analysisError}
        mdhdUtilityOptions={mdhdUtilityOptions}
        selectedMdhdUtility={selectedMdhdUtility}
        onSelectedMdhdUtilityChange={setSelectedMdhdUtility}
      />

      {/* Load Forecasts modal - placeholder for todo 2 */}
      {showLoadForecastsModal && (
        <LoadForecastsModal
          onClose={() => setShowLoadForecastsModal(false)}
          choice={loadForecastsChoice}
          file={feederMapFile}
          onSelect={(choice, file) => {
            setLoadForecastsChoice(choice);
            setFeederMapFile(file || null);
            setShowLoadForecastsModal(false);
          }}
        />
      )}

      {/* Case name modal - placeholder for todo 3 */}
      {showCaseNameModal && (
        <CaseNameModal
          caseName={caseName}
          selectedVehicleCategories={selectedVehicleCategories}
          vehicleCategoryLabels={VEHICLE_CATEGORIES}
          onCaseNameChange={setCaseName}
          onClose={() => setShowCaseNameModal(false)}
          onSubmit={handleCaseNameSubmit}
        />
      )}
    </div>
  );
}

function LoadForecastsModal({ onClose, choice, file, onSelect }) {
  const [localChoice, setLocalChoice] = useState(choice || null);
  const [localFile, setLocalFile] = useState(file);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    setLocalFile(f || null);
  };

  const handleSubmit = () => {
    onSelect(localChoice, localFile);
  };

  const handleBackdropClick = () => onClose();
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="load-forecasts-title"
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
    >
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6" onClick={(e) => e.stopPropagation()}>
        <h2 id="load-forecasts-title" className="text-xl font-bold text-gray-800 mb-4 font-ui">Load Forecasts</h2>
        <p className="text-sm text-gray-600 mb-4">Choose how to map load forecasts:</p>
        <div className="space-y-3 mb-4">
          <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-xl cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              name="load-forecasts"
              checked={localChoice === 'hexagon'}
              onChange={() => setLocalChoice('hexagon')}
              className="text-blue-600 rounded-full"
            />
            <span className="font-ui">Map to standard hexagon</span>
          </label>
          <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-xl cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              name="load-forecasts"
              checked={localChoice === 'feeder'}
              onChange={() => setLocalChoice('feeder')}
              className="text-blue-600 rounded-full"
            />
            <span className="font-ui">Map to feeder</span>
          </label>
        </div>
        {localChoice === 'feeder' && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2 font-ui">Upload feeder map</label>
            <input
              type="file"
              accept=".geojson,.json,.shp,.zip"
              onChange={handleFileChange}
              className="block w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
            {localFile && (
              <p className="mt-2 text-sm text-gray-600 font-ui">
                {localFile.name}
                <button type="button" onClick={() => setLocalFile(null)} className="ml-2 text-red-600 hover:underline">Clear</button>
              </p>
            )}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-50 font-ui">
            Cancel
          </button>
          <button type="button" onClick={handleSubmit} className="px-4 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 font-ui">
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}

function CaseNameModal({ caseName, selectedVehicleCategories, vehicleCategoryLabels, onCaseNameChange, onClose, onSubmit }) {
  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(caseName);
  };

  const handleBackdropClick = () => onClose();
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') onClose();
  };

  const selectedLabels = selectedVehicleCategories
    .map((id) => vehicleCategoryLabels.find((c) => c.id === id)?.label)
    .filter(Boolean);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="case-name-title"
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
    >
      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6" onClick={(e) => e.stopPropagation()}>
        <h2 id="case-name-title" className="text-xl font-bold text-gray-800 mb-4 font-ui">Start analysis</h2>
        {selectedLabels.length > 0 && (
          <p className="text-sm text-gray-600 mb-4 font-ui">
            Analysis will run for: <span className="font-medium text-gray-800">{selectedLabels.join(', ')}</span>
          </p>
        )}
        <label className="block text-sm font-medium text-gray-700 mb-2 font-ui">Case name</label>
        <input
          type="text"
          value={caseName}
          onChange={(e) => onCaseNameChange(e.target.value)}
          placeholder="Enter case name"
          className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent font-ui"
          autoFocus
        />
        <div className="flex justify-end gap-2 mt-6">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-50 font-ui">
            Cancel
          </button>
          <button type="submit" className="px-4 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 font-ui">
            Start analysis
          </button>
        </div>
      </form>
    </div>
  );
}

export default UtilityWorkflow;
